import { apiCall, API_CONFIG } from '../config/api';
import { db } from '../firebase';
import { 
  collection, 
  doc, 
  getDoc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  onSnapshot,
  Timestamp 
} from 'firebase/firestore';

// Performance tracking service
export class PerformanceService {
  constructor(userId) {
    this.userId = userId;
    this.campaignsRef = collection(db, 'campaigns');
    this.performanceRef = collection(db, 'performance');
    this.videoLinksRef = collection(db, 'videoLinks');
    this.analyticsRef = collection(db, 'analytics');
  }

  // Campaign Management
  async getCampaigns() {
    try {
      const q = query(
        this.campaignsRef,
        where('userId', '==', this.userId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      throw error;
    }
  }

  async createCampaign(campaignData) {
    try {
      const docRef = await addDoc(this.campaignsRef, {
        ...campaignData,
        userId: this.userId,
        createdAt: Timestamp.now(),
        status: 'active'
      });
      return { id: docRef.id, ...campaignData };
    } catch (error) {
      console.error('Error creating campaign:', error);
      throw error;
    }
  }

  // Performance Data
  async getPerformanceData(campaignId, filter = 'All') {
    try {
      const q = query(
        this.performanceRef,
        where('userId', '==', this.userId),
        where('campaignId', '==', campaignId),
        where('filter', '==', filter),
        orderBy('date', 'desc'),
        limit(30) // Last 30 days
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching performance data:', error);
      throw error;
    }
  }

  async updatePerformanceData(performanceData) {
    try {
      const docRef = await addDoc(this.performanceRef, {
        ...performanceData,
        userId: this.userId,
        date: Timestamp.now()
      });
      return { id: docRef.id, ...performanceData };
    } catch (error) {
      console.error('Error updating performance data:', error);
      throw error;
    }
  }

  // Video Links Management
  async getVideoLinks(campaignId) {
    try {
      const q = query(
        this.videoLinksRef,
        where('userId', '==', this.userId),
        where('campaignId', '==', campaignId),
        orderBy('createdAt', 'desc')
      );
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching video links:', error);
      throw error;
    }
  }

  async addVideoLink(videoLinkData) {
    try {
      const docRef = await addDoc(this.videoLinksRef, {
        ...videoLinkData,
        userId: this.userId,
        createdAt: Timestamp.now(),
        status: 'pending' // pending, processing, completed, failed
      });
      return { id: docRef.id, ...videoLinkData };
    } catch (error) {
      console.error('Error adding video link:', error);
      throw error;
    }
  }

  // Analytics Integration
  async connectSocialPlatform(platform, credentials) {
    try {
      // This would integrate with actual social media APIs
      const response = await apiCall('/api/connect-platform', {
        method: 'POST',
        body: JSON.stringify({
          platform,
          credentials,
          userId: this.userId
        })
      });
      return response;
    } catch (error) {
      console.error('Error connecting platform:', error);
      throw error;
    }
  }

  async fetchPlatformAnalytics(platform, videoUrl) {
    try {
      const response = await apiCall('/api/fetch-analytics', {
        method: 'POST',
        body: JSON.stringify({
          platform,
          videoUrl,
          userId: this.userId
        })
      });
      return response;
    } catch (error) {
      console.error('Error fetching analytics:', error);
      throw error;
    }
  }

  // Real-time Performance Monitoring
  subscribeToPerformanceUpdates(campaignId, callback) {
    const q = query(
      this.performanceRef,
      where('userId', '==', this.userId),
      where('campaignId', '==', campaignId),
      orderBy('date', 'desc'),
      limit(1)
    );

    return onSnapshot(q, (snapshot) => {
      const updates = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      callback(updates);
    });
  }

  // AI Insights Generation
  async generateAIInsights(campaignId, performanceData) {
    try {
      const response = await apiCall('/api/generate-insights', {
        method: 'POST',
        body: JSON.stringify({
          campaignId,
          performanceData,
          userId: this.userId
        })
      });
      return response;
    } catch (error) {
      console.error('Error generating AI insights:', error);
      throw error;
    }
  }

  // Campaign Goals Management
  async getCampaignGoals(campaignId) {
    try {
      const campaignDoc = await getDoc(doc(this.campaignsRef, campaignId));
      if (campaignDoc.exists()) {
        const campaignData = campaignDoc.data();
        return {
          goals: campaignData.goals || [],
          targetViews: campaignData.targetViews || 0,
          targetCTR: campaignData.targetCTR || 0,
          targetConversion: campaignData.targetConversion || 0,
          targetEngagement: campaignData.targetEngagement || 0,
          startDate: campaignData.startDate,
          endDate: campaignData.endDate
        };
      }
      return {
        goals: [],
        targetViews: 0,
        targetCTR: 0,
        targetConversion: 0,
        targetEngagement: 0
      };
    } catch (error) {
      console.error('Error fetching campaign goals:', error);
      throw error;
    }
  }

  async updateCampaignGoals(campaignId, goalData) {
    try {
      await updateDoc(doc(this.campaignsRef, campaignId), {
        ...goalData,
        updatedAt: Timestamp.now()
      });
      return goalData;
    } catch (error) {
      console.error('Error updating campaign goals:', error);
      throw error;
    }
  }

  // Get campaign with goals
  async getCampaignWithGoals(campaignId) {
    try {
      const campaignDoc = await getDoc(doc(this.campaignsRef, campaignId));
      if (campaignDoc.exists()) {
        return {
          id: campaignDoc.id,
          ...campaignDoc.data()
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching campaign with goals:', error);
      throw error;
    }
  }

  // Performance Metrics Calculation
  calculateMetrics(performanceData) {
    if (!performanceData || performanceData.length === 0) {
      return {
        totalViews: 0,
        avgCTR: 0,
        avgConversion: 0,
        topCreators: [],
        topVideos: []
      };
    }

    const totalViews = performanceData.reduce((sum, data) => sum + (data.views || 0), 0);
    const avgCTR = performanceData.reduce((sum, data) => sum + (data.ctr || 0), 0) / performanceData.length;
    const avgConversion = performanceData.reduce((sum, data) => sum + (data.conversion || 0), 0) / performanceData.length;

    // Group by creator and calculate averages
    const creatorStats = {};
    performanceData.forEach(data => {
      if (data.creator) {
        if (!creatorStats[data.creator.handle]) {
          creatorStats[data.creator.handle] = {
            handle: data.creator.handle,
            avatar: data.creator.avatar,
            totalViews: 0,
            totalCTR: 0,
            totalConversion: 0,
            videoCount: 0
          };
        }
        creatorStats[data.creator.handle].totalViews += data.views || 0;
        creatorStats[data.creator.handle].totalCTR += data.ctr || 0;
        creatorStats[data.creator.handle].totalConversion += data.conversion || 0;
        creatorStats[data.creator.handle].videoCount += 1;
      }
    });

    const topCreators = Object.values(creatorStats)
      .map(creator => ({
        ...creator,
        avgCTR: creator.totalCTR / creator.videoCount,
        avgConversion: creator.totalConversion / creator.videoCount
      }))
      .sort((a, b) => b.avgCTR - a.avgCTR)
      .slice(0, 3);

    return {
      totalViews,
      avgCTR: Math.round(avgCTR * 100) / 100,
      avgConversion: Math.round(avgConversion * 100) / 100,
      topCreators
    };
  }
}

// Export singleton instance
export const performanceService = new PerformanceService();
